import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);     // null = unknown/checking, false = no, object = signed in
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        api.get("/auth/me")
            .then((r) => { if (live) setUser(r.data); })
            .catch(() => { if (live) setUser(false); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, []);

    async function login(email, password) {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }

    async function demoLogin(userId) {
        try {
            const { data } = await api.post("/auth/demo-login", { user_id: userId });
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }

    async function resetDemo() {
        try {
            await api.post("/auth/demo-reset");
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }

    async function logout() {
        try { await api.post("/auth/logout"); } catch (e) { console.debug("logout request failed (clearing session anyway):", e?.message); }
        setUser(false);
    }

    async function refreshUser() {
        try { const { data } = await api.get("/auth/me"); setUser(data); return data; }
        catch { return null; }
    }

    async function changePassword(currentPassword, newPassword) {
        try {
            await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
            await refreshUser();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }

    async function forgotPassword(email) {
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            return { ok: true, message: data?.message };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }

    async function resetPassword(token, newPassword) {
        try {
            const { data } = await api.post("/auth/reset-password", { token, new_password: newPassword });
            return { ok: true, message: data?.message };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }

    return (
        <AuthContext.Provider value={{ user, loading, login, demoLogin, resetDemo, logout, setUser, refreshUser, changePassword, forgotPassword, resetPassword }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
